import { useToast } from 'hooks/useToast';
import { apiClient } from 'api';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface SoftSkillsPayload {
  assessment_id: string;
  [key: string]: string | number | boolean;
}

export interface DeleteSoftSkillsPayload {
  assessment_id: string;
}

/**
 * Hook for submitting soft skills
 */
export const useSoftSkillsEdit = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const patchMutation = useMutation({
    mutationFn: async (payload: SoftSkillsPayload) => {
      const response = await apiClient.patch('/assessment/soft-skill/', payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['softSkills', assessmentId] });
      openToast({
        show: true,
        heading: "Edit Successful",
        text: "Soft skills edited successfully!",
        type: "info",
        timeOut: 5000,
      });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || 'Failed to submit soft skills';
      openToast({
        show: true,
        heading: "Sorry",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    submitSoftSkills: patchMutation.mutate,
    isSubmitting: patchMutation.isPending,
  };
};

/**
 * Hook for deleting soft skills
 */
export const useSoftSkillsDelete = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: async (payload: DeleteSoftSkillsPayload) => {
      const response = await apiClient.delete('/assessment/soft-skill/', {
        data: payload,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['softSkills', assessmentId] });
      openToast({
        show: true,
        heading: "Delete Successful",
        text: "Soft skills deleted successfully!",
        type: "info",
        timeOut: 5000,
      });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || 'Failed to delete soft skills';
      openToast({
        show: true,
        heading: "Delete Failed",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    deleteSoftSkills: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};